// src/components/Confirmacion.js
import React, { useEffect, useState } from 'react';
import { api, endpoints } from '../api/api';
import { useNavigate, useLocation } from 'react-router-dom';
import { t } from '../components/i18n';
import logo from '../assets/LIR-BLANCO.png';
import compassLogo from '../assets/Recurso-8compass-NEGRO.png';
import LangSwitcher from '../components/LangSwitcher';

const Confirmacion = ({ onLogout }) => {
  const navigate = useNavigate();
  const location = useLocation();

  // Responsive: detectar pantalla pequeña
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 600);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 600);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);


  // Idioma desde la URL (consistente con ArticulosList)
  const lang = location.pathname.startsWith('/en') ? 'en' : 'es';
  const dict = (t && t[lang]) ? t[lang] : (t?.es || {});

  const HEADER_HEIGHT = 90;
  const BRAND_RED = '#E93F63';

  // ===== Rehidratación de sujeto =====
  const SUJETO_KEY = 'lir_sujeto';
  const [sujeto, setSujeto] = useState(null);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(SUJETO_KEY);
      if (raw) setSujeto(JSON.parse(raw));
    } catch {}
  }, []);

  // ===== Estados para el panel de respuestas =====
  const [articulos, setArticulos] = useState([]);
  const [respuestas, setRespuestas] = useState({});
  const [statusById, setStatusById] = useState({});
  const [mensaje, setMensaje] = useState('');
  const [showAnswers, setShowAnswers] = useState(false);

  const STORAGE_KEY = sujeto?.id ? `lir_respuestas_${sujeto.id}` : null;

  // Helpers (compartidos con ArticulosList)
  const normKey = (v) => String(v ?? '').trim().toUpperCase();

  const keysForArticle = (a = {}) => {
    const ks = [a.id_articulo, a.codigo, a.code, a.clave, a.id, a.uid, a.slug]
      .map(normKey)
      .filter(Boolean);
    return Array.from(new Set(ks));
  };

  const getServerKey = (a = {}) =>
    normKey(a.id_articulo || a.codigo || a.code || a.clave || a.id);

  const getAnswerForArticle = (a, map) => {
    for (const k of keysForArticle(a)) {
      const v = map[k];
      if (v != null && v !== '') return v;
    }
    return '';
  };

  const getStatusForArticle = (a, map) => {
    for (const k of keysForArticle(a)) {
      const v = map[k];
      if (v) return v;
    }
    return 'idle';
  };

  const toUpperKeyMap = (obj = {}) => {
    const out = {};
    Object.entries(obj).forEach(([k, v]) => (out[normKey(k)] = v));
    return out;
  };

  const fetchServerRespuestas = async (idSujeto) => {
    try {
      const r = await api.get(endpoints.respuestas(idSujeto), {
        params: { _ts: Date.now() },
      });
      if (!Array.isArray(r.data)) return {};
      return r.data.reduce((acc, it) => {
        if (it?.id_articulo != null && it?.calificacion != null) {
          acc[normKey(it.id_articulo)] = it.calificacion;
        }
        return acc;
      }, {});
    } catch {
      return {};
    }
  };

  // Cargar artículos
  useEffect(() => {
    if (!sujeto?.id) return;
    (async () => {
      try {
        const res = await api.get(endpoints.articulos(sujeto.id), {
          params: { _ts: Date.now() },
        });
        const raw = Array.isArray(res.data) ? res.data : [];
        const data = raw.map((a) => {
          const key = getServerKey(a);
          return key && !a.id_articulo ? { ...a, id_articulo: key } : a;
        });
        setArticulos(data);
      } catch {
        setMensaje(dict.error_fetch_articles || 'Error al obtener artículos.');
      }
    })();
  }, [sujeto, dict]);

  // Rehidratación inicial: servidor + local (solo lectura)
  useEffect(() => {
    if (!sujeto?.id) return;
    let mounted = true;
    (async () => {
      const server = await fetchServerRespuestas(sujeto.id);
      let local = {};
      try {

        if (STORAGE_KEY) {
          const raw = localStorage.getItem(STORAGE_KEY);
          local = raw ? JSON.parse(raw) : {};
        }
      } catch {}
      const merged = { ...toUpperKeyMap(local), ...toUpperKeyMap(server) };
      if (!mounted) return;
      setRespuestas(merged);
      setStatusById((s) => {
        const next = { ...s };
        Object.keys(server).forEach((id) => {
          next[normKey(id)] = 'saved';
        });
        return next;
      });
    })();
    return () => {
      mounted = false;
    };
  }, [sujeto, STORAGE_KEY]);

  const handleLogout = () => {
    try {
      localStorage.removeItem(SUJETO_KEY);
      if (STORAGE_KEY) localStorage.removeItem(STORAGE_KEY);
      sessionStorage.removeItem('sujeto');
      sessionStorage.removeItem('lir_submitted');
    } catch {}
    if (typeof onLogout === 'function') onLogout();
    navigate(lang === 'en' ? '/en' : '/es', { replace: true });
  };

  const StatusBadge = ({ st }) => {
    const base = {
      padding: '2px 8px',
      borderRadius: 999,
      fontSize: '.75rem',
      fontWeight: 700,
      border: '1px solid',
      whiteSpace: 'nowrap',
    };
    if (st === 'saved')
      return (
        <span
          style={{
            ...base,
            color: '#34d399',
            borderColor: 'rgba(52,211,153,.5)',
            background: 'rgba(52,211,153,.12)',
          }}
        >
          ✓ {dict.saved || 'Guardado'}
        </span>
      );    if (st === 'saving')
      return (
        <span
          style={{
            ...base,
            color: '#bdbdbd',
            borderColor: 'rgba(189,189,189,.5)',
            background: 'rgba(189,189,189,.12)',
          }}
        >
          {dict.saving || 'Guardando…'}
        </span>
      );
    if (st === 'error')
      return (
        <span
          style={{
            ...base,
            color: '#ff6b6b',
            borderColor: 'rgba(255,107,107,.5)',
            background: 'rgba(255,107,107,.12)',
          }}
        >
          {dict.retry_later || 'Error'}
        </span>
      );
    return (
      <span
        style={{
          ...base,
          color: '#bdbdbd',
          borderColor: 'rgba(189,189,189,.35)',
          background: 'rgba(189,189,189,.08)',
        }}
      >
        —
      </span>
    );
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        background:
          'radial-gradient(1200px 800px at 20% -10%, rgba(233,63,99,0.15) 0%, rgba(18,18,18,0) 40%), radial-gradient(900px 700px at 120% 120%, rgba(255,255,255,0.06) 0%, rgba(18,18,18,0) 35%), #000',
        color: '#fff',
        fontFamily: 'Segoe UI, Arial, sans-serif',
        position: 'relative',
      }}
    >
      {/* Header */}
      <div
        style={{
          position: 'absolute',
          top: 24,
          left: 24,
          right: 24,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '1rem',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <img src={logo} alt="LIR" style={{ height: 36, objectFit: 'contain' }} />
          <div>
            <h1
              style={{
                color: '#fff',
                fontSize: isMobile ? '1rem' : '1.2rem',
                margin: 0,
                fontWeight: 700,
                letterSpacing: '.2px',
              }}
            >
              {dict.hero_title || 'Evaluación exclusiva de artículos legales'}
            </h1>
            <p style={{ color: '#bdbdbd', margin: 0, fontSize: '.9rem' }}>
              {dict.hero_sub || 'Plataforma privada de retroalimentación profesional'}
            </p>
          </div>
        </div>

        {/* Derecha: selector + logo + Back + Logout */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <LangSwitcher />
          <img
            src={compassLogo}
            alt="Compass"
            style={{ height: 36, objectFit: 'contain', opacity: 0.95 }}
          />
          <button
            onClick={() => navigate(lang === 'en' ? '/en/articles' : '/es/articulos')}
            style={{
              background: 'rgba(233,63,99,.15)',
              border: '1px solid rgba(233,63,99,.45)',
              color: '#fff',
              padding: '.35rem .6rem',
              borderRadius: 8,
              cursor: 'pointer',
              fontWeight: 700,
            }}
          >
            {dict.back || (lang === 'en' ? 'Back' : 'Volver')}
          </button>
          <button
            onClick={handleLogout}
            style={{
              background: 'rgba(255,255,255,.08)',
              border: '1px solid #2a2a2a',
              color: '#fff',
              padding: '.35rem .6rem',
              borderRadius: 8,
              cursor: 'pointer',
              fontWeight: 700,
            }}
          >
            {dict.logout || 'Cerrar sesión'}
          </button>            
        </div>
      </div>

      {/* Contenido */}
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flexDirection: 'column',
          paddingTop: HEADER_HEIGHT,
          textAlign: 'center',
        }}
      >
        <h1
          style={{
            fontSize: isMobile ? '2rem' : '2.5rem',
            marginBottom: '1rem',
            color: BRAND_RED,
          }}
        >
          {dict.confirm_title || '¡Gracias por tu evaluación!'}
        </h1>
        <p style={{ fontSize: isMobile ? '1rem' : '1.2rem', color: '#bbb' }}>
          {dict.confirm_sub || 'Tus respuestas fueron registradas correctamente.'}
        </p>
        <button
          onClick={() => setShowAnswers((v) => !v)}
          style={{
            marginTop: '1.5rem',
            backgroundColor: BRAND_RED,
            color: '#fff',
            border: 'none',
            padding: '.9rem 1.2rem',
            borderRadius: 10,
            fontWeight: 700,
            cursor: 'pointer',
          }}
        >
          {showAnswers
            ? dict.hide_answers || (lang === 'en' ? 'Hide answers' : 'Ocultar respuestas')
            : dict.view_answers || (lang === 'en' ? 'View answers' : 'Ver respuestas')}
        </button>
      </div>

      {/* Panel "Current responses" */}
      {showAnswers && (
        <div
          style={{
            position: 'fixed',
            left: 0,
            right: 0,
            bottom: 68,
            margin: '0 auto',
            maxWidth: 1120,
            background: '#111',
            border: '1px solid #2a2a2a',
            borderRadius: 12,
            boxShadow: '0 16px 40px rgba(0,0,0,.5)',
            padding: '1rem',
            zIndex: 12,
            maxHeight: '60vh',
            overflow: 'auto',
            animation: 'fadeIn .2s ease',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 8,
              marginBottom: 8,
            }}
          >
            <h4 style={{ margin: 0, color: '#fff' }}>
              {dict.current_answers || 'Respuestas actuales'}
            </h4>
            <button
              onClick={() => setShowAnswers(false)}
              style={{
                background: 'rgba(255,255,255,.08)',
                border: '1px solid #2a2a2a',
                color: '#fff',
                borderRadius: 8,
                padding: '.35rem .6rem',
                cursor: 'pointer',
              }}
            >
              {dict.close || (lang === 'en' ? 'Close' : 'Cerrar')}
            </button>
          </div>

          <div style={{ display: 'grid', gap: 10 }}>
            {articulos.map((a, i) => {
              const val = getAnswerForArticle(a, respuestas);
              const st = getStatusForArticle(a, statusById);
              return (
                <div
                  key={getServerKey(a) || i}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr auto',
                    gap: 10,
                    alignItems: 'center',
                    padding: '.6rem .75rem',
                    border: '1px solid #2a2a2a',
                    borderRadius: 10,
                    background: '#161616',
                  }}
                >
                  <div style={{ color: '#e5e5e5', fontWeight: 600, lineHeight: 1.35 }}>
                    {i + 1}. {a.titulo}
                    <div style={{ color: '#bdbdbd', fontSize: '.9rem', marginTop: 2 }}>
                      {val
                        ? `${dict.rating || 'Calificación'}: ${val}`
                        : dict.missing || 'Sin calificar'}
                    </div>
                  </div>
                  <StatusBadge st={st} />
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Toast de errores */}
      {mensaje && (
        <div
          role="status"
          style={{
            position: 'fixed',
            right: 16,
            bottom: 86,
            background: 'rgba(255,68,68,.15)',
            border: '1px solid rgba(255,68,68,.35)',
            color: '#ff4444',
            padding: '.75rem 1rem',
            borderRadius: 12,
            backdropFilter: 'blur(6px)',
            animation: 'fadeIn .25s ease',
            zIndex: 15,
          }}
        >
          {mensaje}
        </div>
      )}

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(4px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default Confirmacion;
